/**
 * OTP as a Service — "Vylo OTP" (otp.vylo-tech.com).
 *
 * The PLATFORM-level sellable OTP product: external developers ("clients")
 * authenticate with an API key (`Authorization: Bearer otpsk_...`) and call
 * send/verify; every delivered message is metered at the client's per-message
 * price and billed manually from the platform dashboard.
 *
 * Distinct from `otp.ts`, which is the TENANT storefront-login OTP feature.
 */

import { z } from 'zod';

import { WaInstanceNameSchema } from './whatsapp-gateway.js';

// -----------------------------------------------------------------------------
// Public API inputs (key-authenticated clients)
// -----------------------------------------------------------------------------

/** Destination phone — normalizes to digits (drops +, spaces, dashes). */
export const OtpServicePhoneSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .pipe(z.string().regex(/^\d{6,20}$/, 'expected 6-20 digits'));

/** Who asked for a metered message: the OTP API (`api`), the general message
 *  API (`message`), a tenant's storefront phone login (`erp`), or an operator's
 *  test send (`test`). Every message off a client's number is counted whatever
 *  asked for it — this only labels the row, it never changes billing.
 *
 *  Single source of truth on purpose: the gateway's write-side union, the admin
 *  ledger serializer and the customer log all narrow through this schema, so the
 *  next value added here cannot silently fall back to "api" somewhere. */
export const OtpMessageSourceSchema = z.enum(['api', 'erp', 'test', 'message']);

/** Free-text WhatsApp body for POST /messages/send. 1000 chars is far under
 *  WhatsApp's own 4096-char text limit (a real notification is never truncated)
 *  and twice the message_template ceiling, while still bounding what a stolen
 *  key can broadcast. Trimmed FIRST, so a whitespace-only body is a validation
 *  error rather than a billed blank message. */
const MessageBody = z.string().trim().min(1).max(1000);

/** General (non-OTP) send. The destination field MUST stay named `phone`: the
 *  per-recipient limiter keys off req.body.phone directly, and any other name
 *  would silently collapse every destination into one bucket per client. */
export const OtpServiceMessageSendSchema = z.object({
  phone: OtpServicePhoneSchema,
  message: MessageBody,
});
export type OtpServiceMessageSendInput = z.infer<typeof OtpServiceMessageSendSchema>;

export const OtpServiceSendSchema = z.object({
  phone: OtpServicePhoneSchema,
});
export type OtpServiceSendInput = z.infer<typeof OtpServiceSendSchema>;

export const OtpServiceVerifySchema = z.object({
  phone: OtpServicePhoneSchema,
  code: z.string().trim().regex(/^\d{4,8}$/, 'expected 4-8 digits'),
});
export type OtpServiceVerifyInput = z.infer<typeof OtpServiceVerifySchema>;

/** A real calendar date in YYYY-MM-DD (rejects 2026-13-45 etc.). */
const CalendarDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00.000Z`)), 'invalid calendar date');

/** Optional date window for usage queries (inclusive from, inclusive to; YYYY-MM-DD). */
export const OtpServiceUsageQuerySchema = z.object({
  from: CalendarDate.optional(),
  to: CalendarDate.optional(),
});
export type OtpServiceUsageQuery = z.infer<typeof OtpServiceUsageQuerySchema>;

// -----------------------------------------------------------------------------
// Platform admin inputs
// -----------------------------------------------------------------------------

/** USD money string with up to 4 decimals (house money convention). */
const MONEY_4DP = /^\d+(\.\d{1,4})?$/;
/** Price fits DECIMAL(8,4): max 9999.9999. Keeps out-of-range as a 422, not a 500. */
const PriceString = z
  .string()
  .regex(MONEY_4DP)
  .refine((v) => Number(v) <= 9999.9999, 'max 9999.9999');
/** Payment fits DECIMAL(19,4): cap the integer part at 15 digits. */
const AmountString = z
  .string()
  .regex(/^\d{1,15}(\.\d{1,4})?$/)
  .refine((v) => Number(v) > 0, 'amount must be > 0');

/** Contact details (shared by admin-create + self-registration). */
const ContactEmail = z.string().trim().email().max(191);
const ContactPhone = z.string().trim().max(32);
const CompanyName = z.string().trim().max(160);
/** Account password — 8+ chars, bounded so a giant string can't tie up argon2. */
const AccountPassword = z.string().min(8).max(200);

/** Public self-registration (otp.vylo-tech.com/register) — creates an account
 *  with an API key + a password login but NO WhatsApp sender bound (the operator
 *  provisions that), so a fresh account can sign in + see usage but cannot send
 *  until activated. The email is the login identity (unique across clients). */
export const OtpRegisterSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: ContactEmail,
  password: AccountPassword,
  phone: ContactPhone.optional(),
  company: CompanyName.optional(),
});
export type OtpRegister = z.infer<typeof OtpRegisterSchema>;

/** Dashboard email + password sign-in. Password is only length-bounded (not
 *  strength-checked) on login — the strength gate lives at registration. */
export const OtpLoginSchema = z.object({
  email: ContactEmail,
  password: z.string().min(1).max(200),
});
export type OtpLogin = z.infer<typeof OtpLoginSchema>;

/** Set/replace the signed-in account's password (from the dashboard, authed by
 *  key OR session). Lets a key-only legacy account add a password login. */
export const OtpSetPasswordSchema = z.object({
  password: AccountPassword,
});
export type OtpSetPassword = z.infer<typeof OtpSetPasswordSchema>;

export const OtpClientCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  contactEmail: ContactEmail.nullable().optional(),
  contactPhone: ContactPhone.nullable().optional(),
  company: CompanyName.nullable().optional(),
  /** Evolution instance that delivers this client's codes; null = unconfigured. */
  instanceName: WaInstanceNameSchema.nullable().optional(),
  messageTemplate: z
    .string()
    .trim()
    .min(4)
    .max(500)
    .refine((v) => v.includes('{code}'), 'template must contain {code}')
    .optional(),
  codeLength: z.number().int().min(4).max(8).optional(),
  ttlSeconds: z.number().int().min(60).max(3600).optional(),
  /** USD per delivered message; "0" = free client. Default 0.01. */
  pricePerMessage: PriceString.optional(),
  /** Opt this client into the general message API (default false). */
  messagingEnabled: z.boolean().optional(),
});
export type OtpClientCreate = z.infer<typeof OtpClientCreateSchema>;

export const OtpClientUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  contactEmail: ContactEmail.nullable().optional(),
  contactPhone: ContactPhone.nullable().optional(),
  company: CompanyName.nullable().optional(),
  instanceName: WaInstanceNameSchema.nullable().optional(),
  messageTemplate: z
    .string()
    .trim()
    .min(4)
    .max(500)
    .refine((v) => v.includes('{code}'), 'template must contain {code}')
    .optional(),
  codeLength: z.number().int().min(4).max(8).optional(),
  ttlSeconds: z.number().int().min(60).max(3600).optional(),
  pricePerMessage: z.string().regex(MONEY_4DP).optional(),
  isActive: z.boolean().optional(),
  /** The messaging kill switch — revocable on its own, so an abusive general
   *  sender can be stopped without disabling the account's OTP traffic. */
  messagingEnabled: z.boolean().optional(),
});
export type OtpClientUpdate = z.infer<typeof OtpClientUpdateSchema>;

export const OtpPaymentCreateSchema = z.object({
  /** USD received; positive money string bounded to DECIMAL(19,4). */
  amount: AmountString,
  note: z.string().trim().max(500).nullable().optional(),
  /** Backdate support — ISO datetime; defaults to now. */
  paidAt: z.string().datetime().optional(),
});
export type OtpPaymentCreate = z.infer<typeof OtpPaymentCreateSchema>;

export type OtpPaymentClaimStatus = 'pending' | 'confirmed' | 'rejected' | 'expired';

/** Customer self-service top-up request: "I paid X" (otp.vylo-tech.com/dashboard).
 *  A claim never credits the balance by itself — confirming one inserts a real
 *  otp_service_payments row, exactly like OtpPaymentCreateSchema does. */
/** How the customer paid: a manual transfer from one of the Iraqi wallet apps to the owner's number. */
export const OtpPaymentMethodSchema = z.enum(['fib', 'fastpay', 'qicard', 'other']);
export type OtpPaymentMethod = z.infer<typeof OtpPaymentMethodSchema>;

export const OtpPaymentClaimCreateSchema = z.object({
  amount: AmountString,
  /** Free-text reference — bank transfer ref, "paid cash", etc. */
  note: z.string().trim().max(500).nullable().optional(),
  /** The app the money was sent from. When given, `reference` is required (checked in the service). */
  method: OtpPaymentMethodSchema.nullable().optional(),
  /** The transaction reference the app showed. */
  reference: z.string().max(80).nullable().optional(),
  /** Who sent it, if not obvious: a name or a number. */
  payer: z.string().max(200).nullable().optional(),
});
export type OtpPaymentClaimCreate = z.infer<typeof OtpPaymentClaimCreateSchema>;

/** Rejecting a claim must say why — the note is shown back to the customer. */
export const OtpPaymentClaimRejectSchema = z.object({
  resolutionNote: z.string().trim().min(1).max(500),
});
export type OtpPaymentClaimReject = z.infer<typeof OtpPaymentClaimRejectSchema>;

/** Admin inbox filter. Defaulted to 'pending' at the call site (not here), so a
 *  bare GET with no query string still means "show me what needs action". */
export const OtpPaymentClaimListQuerySchema = z.object({
  status: z.enum(['pending', 'confirmed', 'rejected', 'expired']).optional(),
});
export type OtpPaymentClaimListQuery = z.infer<typeof OtpPaymentClaimListQuerySchema>;

export const OtpMessageListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).max(100000).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  status: z.enum(['sent', 'failed']).optional(),
});
export type OtpMessageListQuery = z.infer<typeof OtpMessageListQuerySchema>;

/** A client's OWN message-log filter (public GET /messages). Same paging as the
 *  admin ledger plus a source filter, which is how a customer separates general
 *  messages from OTP traffic — /usage deliberately does not split by source. */
export const OtpServiceMessageListQuerySchema = OtpMessageListQuerySchema.extend({
  source: OtpMessageSourceSchema.optional(),
});
export type OtpServiceMessageListQuery = z.infer<typeof OtpServiceMessageListQuerySchema>;

// -----------------------------------------------------------------------------
// Responses
// -----------------------------------------------------------------------------

/** A client account as shown in the dashboard — the API key is NEVER included
 *  (only its display prefix); the full key exists solely in create/rotate replies. */
export interface OtpClientView {
  id: string;
  name: string;
  contactEmail: string | null;
  contactPhone: string | null;
  company: string | null;
  /** First characters of the key for display, e.g. "otpsk_3f2a9c". */
  keyPrefix: string;
  instanceName: string | null;
  messageTemplate: string;
  codeLength: number;
  ttlSeconds: number;
  /** USD per message as a money string; "0.0000" = free. */
  pricePerMessage: string;
  isActive: boolean;
  /** Whether POST /messages/send works for this client. OFF by default: free
   *  text off a customer's own WhatsApp number is a spam/ban vector a fixed code
   *  template is not, so the operator opts each account in (and can revoke
   *  messaging alone, without killing the client's live OTP login traffic). */
  messagingEnabled: boolean;
  lastUsedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Create/rotate reply — the ONLY places the full API key is ever revealed. */
export interface OtpClientCreatedView extends OtpClientView {
  apiKey: string;
}

/** Billing rollup for one client (all money fields are USD strings). */
export interface OtpClientStatsView {
  sentCount: number;
  failedCount: number;
  sentThisMonth: number;
  sentToday: number;
  /** Σ unit_price of billable (sent) messages, all time. */
  cost: string;
  paymentsTotal: string;
  /** cost − paymentsTotal; positive = the customer owes. */
  balance: string;
}

/** List-page row: the client plus its rollup. */
export interface OtpClientListItem extends OtpClientView {
  stats: OtpClientStatsView;
}

/** Usage aggregates for a window (admin per-client view AND the public /usage). */
export interface OtpServiceUsageView {
  from: string;
  to: string;
  totalSent: number;
  totalFailed: number;
  /** Cost of billable sends inside the window (USD string). */
  cost: string;
  /** All-time payments and balance (window-independent context for billing). */
  paymentsTotal: string;
  /** All-time subscription fees (Unlimited), part of the balance. "0.0000" when none. */
  chargesTotal: string;
  /** messages + charges - payments; negative = in credit. */
  balance: string;
  pricePerMessage: string;
  days: Array<{ date: string; sent: number; failed: number }>;
}

/** One ledger row (platform admins see the full phone; the UI masks it). */
/** Who asked for the message — see OtpMessageSourceSchema. Inferred from the
 *  schema rather than re-typed, so adding a value there breaks every exhaustive
 *  lookup at compile time instead of quietly mislabelling a row. */
export type OtpMessageSource = z.infer<typeof OtpMessageSourceSchema>;

export interface OtpServiceMessageView {
  id: string;
  phone: string;
  source: OtpMessageSource;
  status: 'sent' | 'failed';
  error: string | null;
  billable: boolean;
  /** USD price snapshot at send time. */
  unitPrice: string;
  createdAt: string;
}

export interface OtpPaymentView {
  id: string;
  amount: string;
  note: string | null;
  paidAt: string;
  createdAt: string;
}

/** A customer's "I paid X" top-up request and its resolution. clientId/clientName
 *  are populated on the admin cross-client inbox; a client's own history omits
 *  them — it never needs its own id echoed back. */
export interface OtpPaymentClaimView {
  id: string;
  clientId?: string;
  clientName?: string;
  amount: string;
  note: string | null;
  status: OtpPaymentClaimStatus;
  expiresAt: string;
  resolvedAt: string | null;
  resolutionNote: string | null;
  /** Set once confirmed — the otp_service_payments row it created. */
  paymentId: string | null;
  /** How it was paid, the app's transaction reference and who sent it (null on older claims). */
  method: OtpPaymentMethod | null;
  reference: string | null;
  payer: string | null;
  createdAt: string;
}

/** Admin-only lifetime rollup: how much has ever been accepted (direct payments
 *  AND confirmed claims — both land in the same otp_service_payments table, so
 *  this is a plain SUM), and how many top-up requests are in the inbox now. */
export interface OtpPlatformOtpTotalsView {
  totalAccepted: string;
  pendingClaimsCount: number;
}

/** Public send reply. */
export interface OtpServiceSendResult {
  sent: true;
  ttlSeconds: number;
}

/** Public verify reply (failures are Problem responses with cause.code). */
export interface OtpServiceVerifyResult {
  valid: true;
}

/** Public general-message reply. There is no provider message id — the gateway
 *  helper discards it — and `sent` means the gateway ACCEPTED the message:
 *  nothing in this product tracks delivery or read receipts. */
export interface OtpServiceMessageSendResult {
  sent: true;
}

/** Why a message failed, as told to the OWNING CLIENT. The ledger stores the
 *  gateway's raw complaint, which is upstream-controlled text; that stays
 *  admin-only and is collapsed to one of these stable codes on the way out. */
export type OtpMessageErrorCode = 'rejected_by_whatsapp' | 'gateway_unreachable' | 'delivery_failed';

/**
 * One row of a client's OWN message log (public GET /messages).
 *
 * Deliberately NOT OtpServiceMessageView: metering attributes every message that
 * leaves the client's WhatsApp number, so this log also holds rows the client's
 * API never asked for — a tenant storefront's login code (`erp`), an operator's
 * test send (`test`). Those recipients are third parties, so their number is
 * masked; only the client's own `api`/`message` traffic shows in full. The
 * message body is never included, because it is never stored.
 */
export interface OtpServiceOwnMessageView {
  id: string;
  /** Full digits for this client's own sends; last 4 only ("•••4567") otherwise. */
  phone: string;
  phoneMasked: boolean;
  source: OtpMessageSource;
  status: 'sent' | 'failed';
  errorCode: OtpMessageErrorCode | null;
  billable: boolean;
  /** USD price snapshot at send time. */
  unitPrice: string;
  createdAt: string;
}

/** One page of a client's own message log. */
export interface OtpServiceMessageListResult {
  items: OtpServiceOwnMessageView[];
  total: number;
  page: number;
  pageSize: number;
}

/** Self-registration reply — the API key is revealed ONCE here. */
export interface OtpRegisterResult {
  apiKey: string;
  keyPrefix: string;
  name: string;
}

/** Email + password sign-in reply — a bearer session token for the dashboard.
 *  The token authenticates the same routes as the API key (/me, /usage, /otp/*)
 *  but is short-lived and, unlike the key, safe to mint from a login form. */
export interface OtpLoginResult {
  token: string;
  /** Token lifetime in seconds (the dashboard re-prompts after it lapses). */
  expiresIn: number;
}

/** `GET /me` — identity + config the self-service dashboard shows the client. */
export interface OtpMeView {
  name: string;
  keyPrefix: string;
  /** True once a WhatsApp sender (Evolution instance) is bound → sends work. */
  configured: boolean;
  pricePerMessage: string;
  /** True when the account has a password set → can use email+password sign-in.
   *  False for key-only accounts (admin-created or pre-password) → the dashboard
   *  offers to set one. */
  hasPassword: boolean;
  /** Whether the general message API (and so Promotions) is switched on for this account. */
  messagingEnabled: boolean;
  /** Pay as you go, or an Unlimited year (see otp-service.plans.ts). */
  plan: { id: 'payg' | 'unlimited'; unlimitedUntil: string | null; unlimitedActive: boolean; period: 'month' | 'year' | null };
  /** Whether Promotions (bulk) can be used now, and if not, the first thing missing. */
  bulk: { available: boolean; reason: null | 'plan_required' | 'messaging_disabled' | 'not_configured' };
  /** The first 20 delivered messages of every account are free (otp-service.plans.ts FREE_MESSAGES). */
  freeMessages: { total: number; used: number; left: number };
}

// -----------------------------------------------------------------------------
// Contacts and Promotions (bulk) -- Unlimited package
// -----------------------------------------------------------------------------

/** A recipient list entry as the dashboard sends it. The server re-normalises and drops what it cannot use. */
export const OtpCampaignAudienceEntrySchema = z.object({
  phone: z.union([z.string().max(40), z.number()]).transform((v) => String(v)),
  name: z.string().trim().max(120).nullish(),
});

/** `POST /plans/unlimited`: which period to buy. Omitted means the year. */
export const OtpPlanBuySchema = z.object({
  period: z.enum(['month', 'year']).optional().default('year'),
});
export type OtpPlanBuy = z.infer<typeof OtpPlanBuySchema>;

export const OTP_CAMPAIGN_MAX_AUDIENCE = 5000;
export const OTP_CAMPAIGN_DEFAULT_DAILY_CAP = 250;

export const OtpCampaignCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  message: z.string().min(1).max(2000),
  nameFallback: z.string().trim().max(40).optional().default(''),
  dailyCap: z.number().int().min(20).max(400).optional().default(OTP_CAMPAIGN_DEFAULT_DAILY_CAP),
  consent: z.boolean(),
  audience: z.array(OtpCampaignAudienceEntrySchema).max(OTP_CAMPAIGN_MAX_AUDIENCE + 500),
});
export type OtpCampaignCreate = z.infer<typeof OtpCampaignCreateSchema>;

const PageNumber = z.coerce.number().int().min(1).max(100000).optional().default(1);

export const OtpCampaignListQuerySchema = z.object({
  page: PageNumber,
  pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type OtpCampaignListQuery = z.infer<typeof OtpCampaignListQuerySchema>;

export const OtpCampaignItemsQuerySchema = z.object({
  status: z.enum(['pending', 'sent', 'failed', 'unknown']).optional(),
  page: PageNumber,
  pageSize: z.coerce.number().int().min(1).max(500).optional().default(100),
});
export type OtpCampaignItemsQuery = z.infer<typeof OtpCampaignItemsQuerySchema>;

export const OtpContactsQuerySchema = z.object({
  page: PageNumber,
  pageSize: z.coerce.number().int().min(1).max(500).optional().default(50),
  q: z.string().trim().max(60).optional(),
  filter: z.enum(['all', 'suppressed']).optional().default('all'),
});
export type OtpContactsQuery = z.infer<typeof OtpContactsQuerySchema>;

export const OtpPhonesSchema = z.object({
  phones: z.array(z.union([z.string().max(40), z.number()]).transform((v) => String(v))).min(1).max(500),
});
export type OtpPhones = z.infer<typeof OtpPhonesSchema>;

export type OtpCampaignStatus = 'draft' | 'running' | 'paused' | 'done' | 'cancelled' | 'halted';
export type OtpCampaignHaltReason = 'plan_ended' | 'messaging_disabled' | 'gateway_failures' | 'not_configured';
export type OtpCampaignItemStatus = 'pending' | 'sent' | 'failed' | 'unknown';

export interface OtpCampaignView {
  id: string;
  name: string;
  message: string;
  nameFallback: string;
  dailyCap: number;
  status: OtpCampaignStatus;
  haltReason: OtpCampaignHaltReason | null;
  total: number;
  sent: number;
  failed: number;
  /** Not yet sent (and, once cancelled, never going to be). */
  pending: number;
  skipped: { invalid: number; duplicate: number; suppressed: number };
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  nextSendAt: string | null;
  etaSeconds: number | null;
}

export interface OtpCampaignItemView {
  phone: string;
  name: string | null;
  status: OtpCampaignItemStatus;
  error: OtpMessageErrorCode | null;
  sentAt: string | null;
}

export interface OtpContactView {
  phone: string;
  name: string | null;
  sent: number;
  failed: number;
  lastAt: string;
  lastStatus: 'sent' | 'failed';
  suppressed: boolean;
}
